import { __awaiter } from "tslib";
import React from 'react';
import LabSimService, { StateContext } from '../services/LabSimService';
import { Guid, ResourceType } from '../helpers/ResourceHelper';
import ProductHelper from '../helpers/ProductHelper';
import SectionSelectionControl from './toc/SectionSelectionControl';
import ResourceFilterControl from './toc/ResourceFilterControl';
import { T } from '@testout/labsim-ui-atomics';
import styles from './TableOfContentsControl.module.less';
import CircleIndeterminateProgress from './circleindeterminateprogress';
import { IntermediateCheckboxState } from './IntermediateCheckbox';
import { decodeUnicodeEscapes } from '../helpers/TextHelper';
export default React.forwardRef((props, ref) => (React.createElement(StateContext.Consumer, null, (stateContext) => (React.createElement(TableOfContentsControl, Object.assign({}, props, { context: stateContext }))))));
export var FilterType;
(function (FilterType) {
    FilterType[FilterType["module"] = 0] = "module";
    FilterType[FilterType["video"] = 1] = "video";
    FilterType[FilterType["demo"] = 2] = "demo";
    FilterType[FilterType["text"] = 3] = "text";
    FilterType[FilterType["sim"] = 4] = "sim";
    FilterType[FilterType["exam"] = 5] = "exam";
})(FilterType || (FilterType = {}));
const defaultFilters = {
    module: false,
    video: false,
    demo: false,
    text: false,
    sim: false,
    exam: false,
};
class TableOfContentsControl extends React.Component {
    constructor(props) {
        super(props);
        this.onResetAll = () => {
            this.setState({
                appliedFilters: Object.assign({}, defaultFilters),
                checkedFilters: Object.assign({}, defaultFilters),
            });
        };
        this.onApplied = () => {
            const checkedFilters = Object.assign({}, this.state.checkedFilters);
            this.setState({
                appliedFilters: checkedFilters,
            });
        };
        this.setCheckboxFiltersEvent = (checkedState, type) => {
            const newFilters = Object.assign({}, this.state.checkedFilters, {
                [FilterType[type]]: checkedState === IntermediateCheckboxState.CHECKED,
            });
            this.setState({
                checkedFilters: newFilters,
            });
        };
        this.dismissFilter = (checkedState, type) => {
            const newFilters = Object.assign({}, this.state.appliedFilters, {
                [FilterType[type]]: false,
            });
            this.setCheckboxFiltersEvent(checkedState, type);
            this.setState({
                appliedFilters: newFilters,
            });
        };
        this.resolvePropResourceToContextReferences = (d, chapters) => {
            const chapter = chapters.find((c) => { var _a; return c.sectionTitle === ((_a = d.chapter) === null || _a === void 0 ? void 0 : _a.sectionTitle); });
            if (chapter && chapter.children) {
                const section = chapter.children.find((c) => { var _a, _b; return ((_a = c.section) === null || _a === void 0 ? void 0 : _a.resourceGuid) === ((_b = d.section) === null || _b === void 0 ? void 0 : _b.resourceGuid); });
                if (section) {
                    if (section.resourceGuid === d.resourceGuid) {
                        return section;
                    }
                    if (section.children) {
                        return section.children.find((c) => c.resourceGuid === d.resourceGuid);
                    }
                }
            }
            return null;
        };
        this.loadChapters = (productVersion) => __awaiter(this, void 0, void 0, function* () {
            this.setState({ loading: true });
            LabSimService.getSections(productVersion)
                .then((productWithDetails) => {
                const chapters = ProductHelper.resolveSectionData(productWithDetails.sections);
                // it's silly but the routing creates new object, so if we have defaults, let us find them by matching ResourceGuid
                let propsSelectedResources = [];
                if (this.props.defaultSelectedResources &&
                    this.props.defaultSelectedResources.length > 0 &&
                    this.state.selectedResources.length === 0) {
                    propsSelectedResources = this.props.defaultSelectedResources
                        .map((d) => {
                        return this.resolvePropResourceToContextReferences(d, chapters);
                    })
                        .filter((c) => c !== null && this.state.selectedResources.indexOf(c) === -1);
                }
                setTimeout(() => {
                    this.setState((state) => ({
                        chapters,
                        selectedResources: [...state.selectedResources, ...propsSelectedResources],
                        loading: false,
                        errorMessage: '',
                    }));
                }, 1000);
            })
                .catch((e) => {
                this.setState({
                    loading: false,
                    errorMessage: `Oops! Something went wrong while loading the table of contents ${e.message}`,
                });
            });
        });
        this.onResourceToggle = (resources, checked) => {
            this.setState((newState) => {
                let selectedResources;
                if (checked) {
                    const resourcesToAdd = resources.filter((r) => this.state.selectedResources.indexOf(r) === -1);
                    selectedResources = [...newState.selectedResources, ...resourcesToAdd];
                }
                else {
                    selectedResources = newState.selectedResources.filter((r) => resources.indexOf(r) === -1);
                }
                return { selectedResources };
            }, () => {
                this.props.onSelectionChange(this.state.selectedResources);
            });
        };
        this.filtersAreNotApplied = () => !this.state.appliedFilters.module &&
            !this.state.appliedFilters.video &&
            !this.state.appliedFilters.demo &&
            !this.state.appliedFilters.text &&
            !this.state.appliedFilters.sim &&
            !this.state.appliedFilters.exam;
        this.filterResources = (resources) => {
            if (this.filtersAreNotApplied())
                return resources;
            const filtered = resources.filter((r) => {
                return ((this.state.appliedFilters.module && r.type === ResourceType.MODULE) ||
                    (this.state.appliedFilters.video && r.type === ResourceType.VIDEO) ||
                    (this.state.appliedFilters.demo && r.type === ResourceType.DEMO) ||
                    (this.state.appliedFilters.text && r.type === ResourceType.TEXT_LESSON) ||
                    (this.state.appliedFilters.sim && r.type === ResourceType.SIMULATION) ||
                    (this.state.appliedFilters.exam && r.type === ResourceType.EXAM));
            });
            return filtered;
        };
        this.determineCheckedState = (section, sectionResources) => {
            const sectionSelectedResources = (section.children || []).filter((c) => this.state.selectedResources.includes(c));
            const filteredSectionSelectedResources = this.filterResources(sectionSelectedResources);
            const filteredResources = this.filterResources(sectionResources);
            const filteredResourcesInOurSection = (section.children || []).filter((c) => filteredResources.includes(c));
            if ((this.filtersAreNotApplied() || this.state.appliedFilters.module) &&
                section.resourceGuid !== Guid.EMPTY) {
                filteredResourcesInOurSection === null || filteredResourcesInOurSection === void 0 ? void 0 : filteredResourcesInOurSection.push(section);
                if (this.state.selectedResources.includes(section))
                    filteredSectionSelectedResources === null || filteredSectionSelectedResources === void 0 ? void 0 : filteredSectionSelectedResources.push(section);
            }
            if ((filteredSectionSelectedResources === null || filteredSectionSelectedResources === void 0 ? void 0 : filteredSectionSelectedResources.length) === filteredResourcesInOurSection.length)
                return IntermediateCheckboxState.CHECKED;
            if (filteredSectionSelectedResources && (filteredSectionSelectedResources === null || filteredSectionSelectedResources === void 0 ? void 0 : filteredSectionSelectedResources.length) > 0)
                return IntermediateCheckboxState.PARTIAL;
            return IntermediateCheckboxState.UNCHECKED;
        };
        this.getTabs = () => {
            var _a;
            return (_a = this.state.chapters) === null || _a === void 0 ? void 0 : _a.map((chapter) => {
                var _a;
                const selectedCount = this.state.selectedResources.reduce((val, r) => (r && r.chapter === chapter ? val + 1 : val), 0);
                const count = selectedCount !== 0 ? selectedCount : '';
                return {
                    tab: (React.createElement(T.Tabs.Tab, { className: styles.tab, automationId: `tab_${chapter.sectionNumber}`, key: chapter.sectionNumber + chapter.resourceGuid, "data-selectedcount": count },
                        React.createElement(T.Text.Span, { automationId: `${chapter.resourceGuid}` },
                            chapter.sectionNumber.length > 0 ? chapter.sectionNumber + ': ' : '',
                            decodeUnicodeEscapes(chapter.sectionTitle)),
                        React.createElement(T.Text.Span, { automationId: 'count', className: styles.count }, count))),
                    panel: (React.createElement(T.Tabs.Panel, { key: chapter.sectionNumber + chapter.resourceGuid, automationId: `panel`, className: styles.panel },
                        React.createElement("div", null,
                            React.createElement(T.Text.H2, { automationId: chapter.resourceGuid }, "Resources"),
                            React.createElement(ResourceFilterControl, { appliedFilters: this.state.appliedFilters, checkedFilters: this.state.checkedFilters, onChange: this.setCheckboxFiltersEvent, onDismiss: this.dismissFilter, onApplied: this.onApplied, onResetAll: this.onResetAll })), (_a = chapter.children) === null || _a === void 0 ? void 0 :
                        _a.map((s) => {
                            const key = s.resourceGuid !== Guid.EMPTY ? s.resourceGuid : s.sectionNumber;
                            const selectedChildren = this.state.selectedResources.filter((c) => (c === null || c === void 0 ? void 0 : c.section) === s);
                            const checkedState = this.determineCheckedState(s, s.children || []);
                            return (React.createElement(SectionSelectionControl, { filtersAreNotApplied: this.filtersAreNotApplied, filterResources: this.filterResources, key: key, section: s, onResourceSelect: this.onResourceToggle, selectedChildren: selectedChildren, appliedFilters: this.state.appliedFilters, checkedState: checkedState }));
                        }))),
                };
            });
        };
        this.state = {
            selectedResources: [],
            loading: true,
            checkedFilters: Object.assign({}, defaultFilters),
            appliedFilters: Object.assign({}, defaultFilters),
            errorMessage: '',
        };
    }
    componentDidMount() {
        var _a, _b;
        if (this.props.selectedVersion)
            this.loadChapters(this.props.selectedVersion);
        else if (this.props.availableVersions) {
            this.loadChapters((_a = this.props.availableVersions[0]) === null || _a === void 0 ? void 0 : _a.productVersionId);
        }
        else
            this.loadChapters((_b = this.props.currentProduct) === null || _b === void 0 ? void 0 : _b.productVersionId);
    }
    shouldComponentUpdate(nextProps, nextState) {
        if (this.props.currentProduct !== nextProps.currentProduct) {
            // clear selected resources
            this.setState({ selectedResources: [] }, () => {
                var _a;
                this.props.onSelectionChange([]); // make sure parents clear resources as well
                this.loadChapters((_a = nextProps.currentProduct) === null || _a === void 0 ? void 0 : _a.productVersionId);
            });
        }
        return true;
    }
    render() {
        if (this.state.loading) {
            return (React.createElement("div", { className: styles.loading },
                React.createElement(T.Text.H1, { automationId: 'loadingProducts', className: styles.loadingTitle }, "Loading..."),
                React.createElement(CircleIndeterminateProgress, null)));
        }
        else if (this.state.errorMessage) {
            return React.createElement(T.Text.Span, { automationId: 'errorMessage' }, this.state.errorMessage);
        }
        else {
            const tabs = this.getTabs();
            return (React.createElement(T.Tabs, { className: styles.tabs, variation: T.Tabs.Variation.Side, textVariation: T.Tabs.TextVariation.NonCaps, automationId: 'linkSelectTabs' },
                React.createElement(T.Tabs.ListContainer, { className: styles.list },
                    React.createElement(T.Tabs.ListHeader, { className: styles.listHeader },
                        React.createElement(T.Text.H2, { automationId: 'chaptersTitle' }, "Chapters")),
                    React.createElement(T.Tabs.List, null, tabs === null || tabs === void 0 ? void 0 : tabs.map((t) => t.tab))), tabs === null || tabs === void 0 ? void 0 :
                tabs.map((p) => p.panel)));
        }
    }
}
//# sourceMappingURL=TableOfContentsControl.js.map